import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { apiClient } from '@api';
import { useTrans } from '@libs/i18n';
import {
  CancelDomainParam,
  CancelDomainResponse,
  GetAssessmentDomainDetailsParam,
  GetAssessmentDomainDetailsQuery,
  GetAssessmentDomainDetailsResponse,
  PreviousAssessmentSummary,
  PreviousDomainSummary,
  SaveDomainBody,
  SaveDomainParam,
  SaveDomainResponse,
  StartDomainParam,
  StartDomainResponse,
} from '@libs/types';

import { getLocalizedErrorMessage } from '../utils/error-utils';

/**
 * Narrowed return type when the caller opts in to either previous-summary
 * flag. The two flags are independent, so the narrowed shape only marks
 * each summary as non-optional when its corresponding flag was passed.
 *
 * Note: even with the flag set, the field can still be absent at runtime
 * (when there is no parent version), so callers must still null-check —
 * the narrowing improves IntelliSense and prevents passing the flag without
 * intent, not as a runtime guarantee.
 */
type GetAssessmentDomainDetailsResponseWithPreviousSummaries =
  GetAssessmentDomainDetailsResponse & {
    previousDomainSummary?: PreviousDomainSummary;
    previousAssessmentSummary?: PreviousAssessmentSummary;
  };

type GetAssessmentDomainDetailsQueryWithPreviousSummaries =
  GetAssessmentDomainDetailsQuery & {
    includePreviousDomainSummary?: true;
    includePreviousAssessmentSummary?: true;
  };

// ============================================================================
// SERVICE FUNCTIONS - Domain Operations
// ============================================================================

/**
 * Start a domain (sets status to IN_PROGRESS)
 * Input: assessmentId (string), assessmentDomainId (string)
 * Output: Updated domain with IN_PROGRESS status
 */
async function startDomain(params: StartDomainParam): Promise<StartDomainResponse> {
  const response = await apiClient.post<StartDomainResponse>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/start`,
  );

  return response.data;
}

/**
 * Get assessment domain details
 * Input: assessmentId (string), assessmentDomainId (string), includeDocuments (boolean)
 * Output: Domain details with framework, fields, and optional documents
 *
 * Overloaded so callers passing `includePreviousVersion: true` get a return
 * type where `previousVersion` is non-optional.
 */
async function getAssessmentDomainDetails(
  params: GetAssessmentDomainDetailsParam,
  query: GetAssessmentDomainDetailsQueryWithPreviousSummaries,
  signal?: AbortSignal,
): Promise<GetAssessmentDomainDetailsResponseWithPreviousSummaries>;
async function getAssessmentDomainDetails(
  params: GetAssessmentDomainDetailsParam,
  query?: GetAssessmentDomainDetailsQuery,
  signal?: AbortSignal,
): Promise<GetAssessmentDomainDetailsResponse>;
async function getAssessmentDomainDetails(
  params: GetAssessmentDomainDetailsParam,
  query?: GetAssessmentDomainDetailsQuery,
  signal?: AbortSignal,
): Promise<GetAssessmentDomainDetailsResponse> {
  const queryParams = new URLSearchParams();

  if (query?.includeAssessment) {
    queryParams.append('includeAssessment', query.includeAssessment.toString());
  }

  if (query?.includePreviousDomainSummary) {
    queryParams.append('includePreviousDomainSummary', query.includePreviousDomainSummary.toString());
  }

  if (query?.includePreviousAssessmentSummary) {
    queryParams.append('includePreviousAssessmentSummary', query.includePreviousAssessmentSummary.toString());
  }

  const response = await apiClient.get<GetAssessmentDomainDetailsResponse>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainIdentifier}`,
    {
      params: queryParams,
      signal,
    },
  );

  return response.data;
}

/**
 * Save domain progress (sets status to COMPLETED)
 * Input: assessmentId, assessmentDomainId, isSkipped, isApplicable
 * Output: Updated domain with COMPLETED status
 */
async function saveDomain(
  params: SaveDomainParam,
  data: SaveDomainBody,
): Promise<SaveDomainResponse> {
  const response = await apiClient.post<SaveDomainResponse>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/save`,
    data,
  );

  return response.data;
}

/**
 * Cancel domain progress (reverts status to NOT_STARTED)
 * Input: assessmentId (string), assessmentDomainId (string)
 * Output: Updated domain with NOT_STARTED status
 */
async function cancelDomain(params: CancelDomainParam): Promise<CancelDomainResponse> {
  const response = await apiClient.post<CancelDomainResponse>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/cancel`,
  );

  return response.data;
}

// ============================================================================
// REACT QUERY HOOKS - Domain Operations
// ============================================================================

export const useStartDomain = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<StartDomainResponse, Error, StartDomainParam>({
    mutationFn: startDomain,
    onSuccess: (_data, variables) => {

      const queryKey = [
        'assessments',
        variables.assessmentId,
        'domains',
      ];


      // Invalidate domain details
      queryClient.invalidateQueries({
        queryKey: queryKey,
        // refetchType: 'all',
      });

      // Refresh the layout's assessment-by-id cache (stepper colors, completion %).
      queryClient.invalidateQueries({
        queryKey: ['assessments', { assessmentId: variables.assessmentId }],
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to start domain');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};


export function useGetAssessmentDomainDetails(
  params: GetAssessmentDomainDetailsParam,
  query: GetAssessmentDomainDetailsQueryWithPreviousSummaries,
): UseQueryResult<GetAssessmentDomainDetailsResponseWithPreviousSummaries>;
export function useGetAssessmentDomainDetails(
  params: GetAssessmentDomainDetailsParam,
  query?: GetAssessmentDomainDetailsQuery,
): UseQueryResult<GetAssessmentDomainDetailsResponse>;
export function useGetAssessmentDomainDetails(
  params: GetAssessmentDomainDetailsParam,
  query?: GetAssessmentDomainDetailsQuery,
) {
  return useQuery<GetAssessmentDomainDetailsResponse>({
    queryKey: ['assessments', params.assessmentId, 'domains', params.assessmentDomainIdentifier, params, query],
    queryFn: ({ signal }) => getAssessmentDomainDetails(params, query, signal),
    enabled: !!params.assessmentId && !!params.assessmentDomainIdentifier,
  });
}

export const useSaveDomain = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    SaveDomainResponse,
    Error,
    { params: SaveDomainParam; data: SaveDomainBody }
  >({
    mutationFn: ({ params, data }) => saveDomain(params, data),
    onSuccess: (_data, variables) => {

      const queryKey = [
        'assessments',
        variables.params.assessmentId,
        'domains',
      ];

      // Invalidate domain details
      queryClient.invalidateQueries({
        queryKey: queryKey,
        // refetchType: 'all',
      });

      // Refresh the layout's assessment-by-id cache (stepper colors, completion %).
      queryClient.invalidateQueries({
        queryKey: ['assessments', { assessmentId: variables.params.assessmentId }],
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to save domain');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useCancelDomain = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<CancelDomainResponse, Error, CancelDomainParam>({
    mutationFn: cancelDomain,
    onSuccess: (_data, variables) => {
      const queryKey = [
        'assessments',
        variables.assessmentId,
        'domains',
      ];
      queryClient.invalidateQueries({ queryKey: queryKey });

      // Refresh the layout's assessment-by-id cache (stepper colors, completion %).
      queryClient.invalidateQueries({
        queryKey: ['assessments', { assessmentId: variables.assessmentId }],
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to cancel domain');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};
